import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, ClipboardList, CheckSquare, ArrowLeft, List, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import NightworkRequestForm from '../components/Nightwork/NightworkRequestForm';
import NightworkRequestList from '../components/Nightwork/NightworkRequestList';
import NightworkRequestDetail from '../components/Nightwork/NightworkRequestDetail';
import NightworkAssignmentList from '../components/Nightwork/NightworkAssignmentList';
import NightworkAmendmentDiff from '../components/Nightwork/NightworkAmendmentDiff';
import { CountBadge } from '../components/UI/CountBadge';
import { useTabPersistence } from '../hooks/usePersistence';
import { staffAPI } from '../services/api';

const NightworkManagement = () => {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: staffAPI.getCurrentUser,
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const { data: pendingData } = useQuery({
    queryKey: ['nightwork-pending-count'],
    queryFn: staffAPI.getNightworkPendingApproval,
    select: (r) => r.data,
    staleTime: 30_000,
  });

  const canRequestNightwork = user?.can_request_nightwork;
  const isApprover = user?.is_nightwork_approver;
  const isAdmin = user?.has_admin_access;
  const isWorker = user?.employment_classification === 'worker';

  const validTabs = useMemo(() => {
    const tabs = [];
    if (isAdmin) tabs.push('all-requests', 'pending-approval');
    if (canRequestNightwork) {
      if (!tabs.includes('all-requests')) tabs.push('my-requests');
      tabs.push('new-request');
    }
    if (isApprover && !tabs.includes('pending-approval')) tabs.push('pending-approval');
    if (isWorker && !canRequestNightwork && !isApprover && !isAdmin) tabs.push('my-assignments');
    if (tabs.length === 0) tabs.push('my-assignments');
    return tabs;
  }, [canRequestNightwork, isApprover, isAdmin, isWorker]);

  const defaultTab = useMemo(() => {
    if (isAdmin) return 'all-requests';
    if (canRequestNightwork) return 'my-requests';
    if (isApprover) return 'pending-approval';
    return 'my-assignments';
  }, [isAdmin, canRequestNightwork, isApprover]);
  const [activeTab, setActiveTab] = useTabPersistence('nightwork', defaultTab, validTabs);
  const [selectedRequestId, setSelectedRequestId] = React.useState(null);
  // Generalized to serve amendment drafts, plain v1 drafts, and in-place
  // edits of pending requests — see the nightwork pending-edit feature.
  const [editingRequestId, setEditingRequestId] = React.useState(null);

  const pendingCount = Array.isArray(pendingData) ? pendingData.length : pendingData?.count || 0;

  // Lightweight metadata for the request being edited, so the edit view's
  // header/diff panel can adapt to draft vs. amendment vs. pending-edit —
  // shares its query key/cache with NightworkRequestForm & NightworkRequestDetail.
  const { data: editingRequestMeta } = useQuery({
    queryKey: ['nightwork-request', editingRequestId],
    queryFn: () => staffAPI.getNightworkRequest(editingRequestId),
    select: (r) => r.data,
    enabled: !!editingRequestId,
    staleTime: 15_000,
  });
  const isPendingEdit = ['pending_deputy_hod', 'pending_hod'].includes(editingRequestMeta?.status);
  const isAmendmentEdit = (editingRequestMeta?.amendment_version || 1) > 1;

  const handleRequestCreated = () => {
    setEditingRequestId(null);
    setActiveTab(isAdmin ? 'all-requests' : 'my-requests');
  };

  const handleViewDetail = (requestId) => {
    setEditingRequestId(null);
    setSelectedRequestId(requestId);
  };

  const handleBackToList = () => {
    setSelectedRequestId(null);
    setEditingRequestId(null);
  };

  const handleEditRequest = (requestId) => {
    setSelectedRequestId(null);
    setEditingRequestId(requestId);
  };

  const handleBackFromEdit = () => {
    setEditingRequestId(null);
  };

  // Edit view — amendment draft, plain v1 draft, or in-place pending edit
  if (editingRequestId) {
    return (
      <DashboardLayout>
        <div className="space-y-6 bg-gray-50 min-h-screen">
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleBackFromEdit}
                aria-label="Back to Nightwork Requests"
                title="Back to Nightwork Requests"
                className="icon-button flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-subsection-header">
                  {isPendingEdit ? 'Edit Pending Request' : isAmendmentEdit ? 'Edit Amendment' : 'Edit Draft Request'}
                </h1>
                <p className="text-body mt-1">
                  {isPendingEdit
                    ? 'Update the request details. Changes to schedule, personnel, mode, or project will reset the approval chain.'
                    : isAmendmentEdit
                      ? 'Modify the nightwork request details, then submit for re-approval'
                      : 'Modify the nightwork request details, then submit for approval'}
                </p>
              </div>
            </div>
          </div>

          {isAmendmentEdit && <NightworkAmendmentDiff amendmentId={editingRequestId} />}

          <NightworkRequestForm
            editRequestId={editingRequestId}
            onSuccess={handleRequestCreated}
            currentUser={user}
          />
        </div>
      </DashboardLayout>
    );
  }

  // Detail view
  if (selectedRequestId) {
    return (
      <DashboardLayout>
        <div className="space-y-6 bg-gray-50 min-h-screen">
          <NightworkRequestDetail
            requestId={selectedRequestId}
            onBack={handleBackToList}
            currentUser={user}
            onViewDetail={handleViewDetail}
            onEditAmendment={handleEditRequest}
          />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        {/* Page Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <Link
              to="/self-service"
              aria-label="Back to Self-Service"
              title="Back to Self-Service"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-subsection-header">Nightwork Requests</h1>
              <p className="text-body mt-1">Request, manage, and track nightwork assignments</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
          {isAdmin && (
            <button
              onClick={() => setActiveTab('all-requests')}
              className={`tab-btn ${activeTab === 'all-requests' ? 'active' : ''}`}
            >
              <List className="w-4 h-4" />
              All Requests
            </button>
          )}
          {canRequestNightwork && (
            <>
              {!isAdmin && (
                <button
                  onClick={() => setActiveTab('my-requests')}
                  className={`tab-btn ${activeTab === 'my-requests' ? 'active' : ''}`}
                >
                  <ClipboardList className="w-4 h-4" />
                  My Requests
                </button>
              )}
              <button
                onClick={() => setActiveTab('new-request')}
                className={`tab-btn ${activeTab === 'new-request' ? 'active' : ''}`}
              >
                <Plus className="w-4 h-4" />
                New Request
              </button>
            </>
          )}
          {(isApprover || isAdmin) && (
            <button
              onClick={() => setActiveTab('pending-approval')}
              className={`tab-btn ${activeTab === 'pending-approval' ? 'active' : ''}`}
            >
              <CheckSquare className="w-4 h-4" />
              Pending Approval
              {pendingCount > 0 && (
                <CountBadge count={pendingCount} theme="orange" active={activeTab === 'pending-approval'} />
              )}
            </button>
          )}
          {isWorker && !canRequestNightwork && !isApprover && !isAdmin && (
            <button
              onClick={() => setActiveTab('my-assignments')}
              className={`tab-btn ${activeTab === 'my-assignments' ? 'active' : ''}`}
            >
              <Calendar className="w-4 h-4" />
              My Assignments
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'new-request' && canRequestNightwork && (
          <NightworkRequestForm onSuccess={handleRequestCreated} currentUser={user} />
        )}
        {activeTab === 'my-requests' && (
          <NightworkRequestList
            mode="my-requests"
            onViewDetail={handleViewDetail}
            currentUser={user}
          />
        )}
        {activeTab === 'all-requests' && isAdmin && (
          <NightworkRequestList
            mode="all-requests"
            onViewDetail={handleViewDetail}
            currentUser={user}
          />
        )}
        {activeTab === 'pending-approval' && (isApprover || isAdmin) && (
          <NightworkRequestList
            mode="pending-approval"
            onViewDetail={handleViewDetail}
            currentUser={user}
          />
        )}
        {activeTab === 'my-assignments' && (
          <NightworkAssignmentList currentUser={user} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default NightworkManagement;
